import { useCallback, useEffect, useState } from 'react';
import { adminApi } from './api';
import ModuleQuickCreate from './ModuleQuickCreate';
import StatusToast from './StatusToast';
import BulkCatalogImport from './BulkCatalogImport';
import useAdminConfirm from './useAdminConfirm';

export default function ModulesManager({ onChanged, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ matiere: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { askConfirm, confirmDialog } = useAdminConfirm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setItems(await adminApi.modules());
    } catch (err) {
      if (err.status === 401) onUnauthorized?.();
      else setError(err.message || 'Chargement des matières impossible');
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((item) => {
    if (!showInactive && !item.is_active) return false;
    const term = search.toLocaleLowerCase('fr').trim();
    return !term || [item.matiere, item.slug, item.description]
      .some((s) => String(s || '').toLocaleLowerCase('fr').includes(term));
  });

  async function save(item) {
    if (busy || !draft.matiere.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminApi.updateModule(item.id, {
        matiere: draft.matiere,
        description: draft.description,
      });
      setEditing(null);
      await load();
      await onChanged?.();
      setNotice('Matière mise à jour. Son identifiant technique reste inchangé.');
    } catch (err) {
      if (err.status === 401) onUnauthorized?.();
      else setError(err.message || 'Modification impossible');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(item) {
    const next = !item.is_active;
    if (!(await askConfirm({
      title: next ? 'Réactiver cette matière ?' : 'Désactiver cette matière ?',
      description: next
        ? `La matière « ${item.matiere} » sera réactivée.`
        : `La matière « ${item.matiere} » sera désactivée. Les contenus associés seront masqués aux candidats, sans suppression.`,
      confirmLabel: next ? 'Réactiver la matière' : 'Désactiver la matière',
      intent: next ? 'default' : 'danger',
    }))) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminApi.updateModule(item.id, { is_active: next });
      await load();
      await onChanged?.();
      setNotice(next ? 'Matière activée.' : 'Matière désactivée, sans suppression des contenus.');
    } catch (err) {
      if (err.status === 401) onUnauthorized?.();
      else setError(err.message || 'Action impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bo-stack">
      {confirmDialog}
      <header className="bo-panel__header">
        <div>
          <p className="bo-eyebrow">Contenu pédagogique</p>
          <h2>Matières</h2>
          <p className="bo-muted">
            Référentiel central utilisé dans Cours, Ressources et Quiz.
            Les nouvelles matières apparaissent automatiquement dans leurs listes.
          </p>
        </div>
        <ModuleQuickCreate
          onCreated={async () => {
            await load();
            await onChanged?.();
            setNotice('Matière créée et disponible pour les contenus.');
          }}
        />
      </header>
      <BulkCatalogImport kind="modules" onImported={async () => { await load(); await onChanged?.(); }} />
      {error && <div className="bo-alert bo-alert--error" role="alert">{error}</div>}
      <StatusToast message={notice} />
      <section className="bo-panel">
        <div className="bo-panel__header">
          <h3>Référentiel des matières ({filtered.length})</h3>
          <button type="button" className="bo-icon-action" onClick={load}
            aria-label="Actualiser la liste des matières" title="Actualiser la liste des matières">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 11a8 8 0 0 0-14.9-4M4 4v4h4M4 13a8 8 0 0 0 14.9 4M20 20v-4h-4" />
            </svg>
          </button>
        </div>
        <div className="bo-module-filters">
          <label>Rechercher une matière
            <input className="bo-input" value={search}
              onChange={(e) => setSearch(e.target.value)} placeholder="Nom ou description..." />
          </label>
          <label className="bo-module-toggle">
            <input type="checkbox" checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)} />
            Afficher les matières désactivées
          </label>
        </div>
        {loading ? <p className="bo-muted">Chargement...</p> : (
          <div className="bo-table-wrap bo-module-table-wrap">
            <table className="bo-table">
              <thead>
                <tr><th>Matière</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {editing === item.id ? (
                        <div className="bo-module-edit">
                          <label>Nom
                            <input className="bo-input" value={draft.matiere}
                              onChange={(e) => setDraft((v) => ({ ...v, matiere: e.target.value }))} />
                          </label>
                          <label>Description
                            <textarea className="bo-input" value={draft.description}
                              onChange={(e) => setDraft((v) => ({ ...v, description: e.target.value }))} />
                          </label>
                        </div>
                      ) : (
                        <><strong>{item.matiere}</strong>
                        {item.description && <small className="bo-muted bo-block">{item.description}</small>}</>
                      )}
                    </td>
                    <td><span className={item.is_active ? 'bo-badge bo-badge--published' : 'bo-badge bo-badge--archived'}>{item.is_active ? 'Active' : 'Désactivée'}</span></td>
                    <td className="bo-module-actions-cell">
                      <div className="bo-actions bo-module-row-actions">
                        {editing === item.id ? (
                          <>
                            <button type="button" className="bo-button bo-button--primary bo-button--compact"
                              disabled={busy || !draft.matiere.trim()} onClick={() => save(item)}>Enregistrer</button>
                            <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                              onClick={() => setEditing(null)}>Annuler</button>
                          </>
                        ) : (
                          <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                            disabled={busy} onClick={() => {
                              setEditing(item.id);
                              setDraft({ matiere: item.matiere, description: item.description || '' });
                            }}>Modifier</button>
                        )}
                        <button type="button" disabled={busy} onClick={() => toggle(item)}
                          className="bo-button bo-button--ghost bo-button--compact">
                          {item.is_active ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={3}>Aucune matière trouvée.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
